import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import api from "../services/api";
import WishlistHeart from "../components/WishlistHeart";
import "react-toastify/dist/ReactToastify.css";
import "../css/shop.css";

import usePageSeo from "../hooks/usePageSeo";
// ─── Product Card ─────────────────────────────────────────────────────────────
const PCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const price    = product.is_on_sale ? product.sale_price : (product.sell_price || 0);
  const oldPrice = product.is_on_sale ? (product.compare_price || product.sell_price) : null;
  const discount = oldPrice && price < oldPrice
    ? Math.round(((oldPrice - price) / oldPrice) * 100) : null;
  const isOOS    = (product.stock_quantity ?? 1) === 0;
  const image    = product.imageUrl || "/images/default-product.jpg";

  return (
    <div className="pcard" onClick={() => navigate(`/product/${product._id}`)}>
      <div className="pcard-img-wrap">
        <img src={image} alt={product.name} className="pcard-img" loading="lazy" decoding="async"
          onError={(e) => { e.target.src = "/images/default-product.jpg"; }} />
        {discount > 0 && <span className="pcard-badge">-{discount}%</span>}
        <WishlistHeart productId={product._id} />
      </div>
      <div className="pcard-body">
        <div className="pcard-name">{product.name}</div>
        {product.weight && <div className="pcard-weight">{product.weight}</div>}
        <div className="pcard-footer">
          <div className="pcard-prices">
            <span className="pcard-price">₩{price.toLocaleString()}</span>
            {oldPrice && <span className="pcard-old">₩{oldPrice.toLocaleString()}</span>}
          </div>
          {isOOS
            ? <span className="pcard-oos">{t("product.outOfStock")}</span>
            : (
              <button
                className="pcard-add-btn"
                onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
                aria-label="Add to cart"
              >+</button>
            )
          }
        </div>
      </div>
    </div>
  );
};

// ─── Main Shop ────────────────────────────────────────────────────────────────
const Shop = () => {
  usePageSeo({
    title: "Shop All Halal Groceries Online | FMart Korea",
    description: "Browse every halal product at FMart Korea — halal meat, rice, spices, dates, frozen foods and South Asian groceries. Delivered nationwide across South Korea. 한국 전역 배송.",
  });

  const [products,      setProducts]      = useState([]);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [sortBy,        setSortBy]        = useState("default");
  const [maxPrice,      setMaxPrice]      = useState(200000);
  const [categories,    setCategories]    = useState([]);
  const [selectedCat,   setSelectedCat]   = useState("all");
  const [sidebarOpen,   setSidebarOpen]   = useState(false);

  // ✅ CartContext.addToCart — same one used in HomePage (works correctly)
  const { addToCart }       = useCart();
  const { isAuthenticated } = useContext(AuthContext);
  const navigate            = useNavigate();
  const { t }               = useTranslation();

  // ─── Fetch categories for sidebar ────────────────────────────────────────
  useEffect(() => {
    api.get("/categories")
      .then(res => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // ─── Fetch products ───────────────────────────────────────────────────────
  const fetchProducts = async (page) => {
    setLoading(true);
    try {
      const response = await api.get(`/productsOP/all_products?page=${page}`);
      if (response.data) {
        setProducts(response.data.products || []);
        setTotalPages(response.data.pages || 1);
        setTotalProducts(response.data.total || 0);
        setCurrentPage(response.data.page || 1);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error(t("shop.loadFailed"), { position: "top-right", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(currentPage); }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber !== currentPage && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ─── FIX 1: Add to cart ───────────────────────────────────────────────────
  // Uses CartContext.addToCart(productId, qty) — same as HomePage
  // CartContext handles the API call to /cart/add internally + refetches cart
  // Do NOT call api.post("/cart") separately — that was the bug
  const handleAddToCart = async (product) => {
    const productId = product?._id;
    if (!productId) {
      toast.error(t("product.invalidProduct"));
      return;
    }
    try {
      await addToCart(productId, 1);
      toast.success(t("product.addedToCartNamed", { name: product.name }), { position: "top-right", autoClose: 1500 });
    } catch (error) {
      console.error("Error adding product to cart:", error);
      toast.error(t("product.addToCartFailed"), { position: "top-right", autoClose: 3000 });
    }
  };

  // ─── FIX 2: Category filter navigates to /categories/:id/products ─────────
  // Products are paginated server-side so client-side category filter won't work.
  // Selecting a category navigates to the Category page which fetches by category.
  const handleCategorySelect = (catId) => {
    setSelectedCat(catId);
    if (catId !== "all") {
      navigate(`/categories/${catId}/products`);
    }
  };

  // ─── Client-side sort + price filter only ────────────────────────────────
  const filteredProducts = products
    .filter(p => {
      const price = p.is_on_sale ? p.sale_price : p.sell_price;
      return (price || 0) <= maxPrice;
    })
    .sort((a, b) => {
      const pa = a.is_on_sale ? a.sale_price : a.sell_price;
      const pb = b.is_on_sale ? b.sale_price : b.sell_price;
      if (sortBy === "price_asc")  return pa - pb;
      if (sortBy === "price_desc") return pb - pa;
      if (sortBy === "name_asc")   return a.name.localeCompare(b.name);
      return 0;
    });

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || (p >= currentPage - 2 && p <= currentPage + 2));

  return (
    <div className="shop-page">
      <ToastContainer />

      {/* ─── Header ─── */}
      <div className="shop-page-header">
        <div className="shop-header-inner">
          <div>
            <h1 className="shop-page-title">{t("shop.title")}</h1>
            {totalProducts > 0 && (
              <p className="shop-meta-text">{t("shop.available", { count: totalProducts })}</p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <button className="mobile-filter-btn" onClick={() => setSidebarOpen(v => !v)}>
              ⚙ {sidebarOpen ? t("shop.hideFilters") : t("shop.filters")}
            </button>
            <div className="shop-sort-row">
              <span>{t("shop.sortBy")}</span>
              <select className="shop-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="default">{t("shop.sortPopularity")}</option>
                <option value="price_asc">{t("shop.sortPriceAsc")}</option>
                <option value="price_desc">{t("shop.sortPriceDesc")}</option>
                <option value="name_asc">{t("shop.sortNameAsc")}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="shop-layout">

        {/* ─── Sidebar ─── */}
        <aside className={`shop-sidebar ${sidebarOpen ? "sidebar-visible" : ""}`}>
          <div className="sidebar-section">
            <span className="sidebar-head">{t("shop.category")}</span>
            <div className="sidebar-radio-list">
              <label className="sidebar-radio-label">
                <input type="radio" name="cat" value="all"
                  checked={selectedCat === "all"}
                  onChange={() => handleCategorySelect("all")} />
                {t("shop.allCategories")}
              </label>
              {categories.map(cat => (
                <label key={cat._id} className="sidebar-radio-label">
                  <input type="radio" name="cat" value={cat._id}
                    checked={selectedCat === cat._id}
                    onChange={() => handleCategorySelect(cat._id)} />
                  {cat.name}
                </label>
              ))}
            </div>
          </div>

          <div className="sidebar-divider" />

          <div className="sidebar-section">
            <span className="sidebar-head">{t("shop.maxPrice")}</span>
            <input type="range" className="price-range"
              min={0} max={200000} step={5000}
              value={maxPrice} onChange={e => setMaxPrice(Number(e.target.value))} />
            <div className="price-range-labels">
              <span>₩0</span>
              <span>₩{maxPrice.toLocaleString()}</span>
            </div>
          </div>

          <div className="sidebar-divider" />

          <button className="clear-btn" onClick={() => {
            setSelectedCat("all");
            setMaxPrice(200000);
            setSortBy("default");
          }}>
            {t("shop.clearFilters")}
          </button>
        </aside>

        {/* ─── Products ─── */}
        <div>
          {loading ? (
            <div className="shop-loading-wrap">
              <div className="shop-spinner" />
              <span>{t("shop.loading")}</span>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="shop-loading-wrap">
              <span style={{ fontSize: "2rem" }}>🔍</span>
              <span>{t("shop.noMatch")}</span>
            </div>
          ) : (
            <>
              <div className="shop-products-grid">
                {filteredProducts.map(product => (
                  <PCard key={product._id} product={product} onAddToCart={handleAddToCart} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="shop-pagination">
                  <button className="page-btn" disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}>{t("shop.prev")}</button>

                  {pageNumbers.map((page, idx) => {
                    const prev = pageNumbers[idx - 1];
                    return (
                      <React.Fragment key={page}>
                        {prev && page - prev > 1 && <span className="page-ellipsis">…</span>}
                        <button className={`page-btn ${currentPage === page ? "active" : ""}`}
                          onClick={() => handlePageChange(page)}>{page}</button>
                      </React.Fragment>
                    );
                  })}

                  <button className="page-btn" disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}>{t("shop.next")}</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;